/**
 * Sistema centralizado de logging com persistência em banco de dados
 * Remove console.log em produção e fornece logging estruturado
 */

import { supabase } from '../lib/supabase';

type LogLevel = 'info' | 'warn' | 'error' | 'debug';

type EventType = 'loaded' | 'modified' | 'saved' | 'restored' | 'deleted' | 'created' | 'preview' | 'error';

type Severity = 'info' | 'warning' | 'error' | 'critical';

interface LogEntry {
  level: LogLevel;
  message: string;
  data?: unknown;
  timestamp: string;
}

interface ThemeAuditLog {
  themeId?: string;
  themeName: string;
  eventType: EventType;
  eventDescription: string;
  userAction?: string;
  changesMade?: Record<string, unknown>;
  snapshotBefore?: Record<string, unknown>;
  snapshotAfter?: Record<string, unknown>;
  metadata?: Record<string, unknown>;
  severity?: Severity;
}

class Logger {
  private isDevelopment = import.meta.env.DEV;
  private logs: LogEntry[] = [];
  private maxLogs = 100;
  private themeAuditEnabled = true;

  private log(level: LogLevel, message: string, data?: unknown) {
    const entry: LogEntry = {
      level,
      message,
      data,
      timestamp: new Date().toISOString(),
    };

    // Armazena logs em memória
    this.logs.push(entry);
    if (this.logs.length > this.maxLogs) {
      this.logs.shift();
    }

    // Em desenvolvimento, exibe no console
    if (this.isDevelopment) {
      const style = this.getConsoleStyle(level);
      const prefix = this.getPrefix(level);
      
      if (data) {
        console.log(`%c${prefix}`, style, message, data);
      } else {
        console.log(`%c${prefix}`, style, message);
      }
    }

    // Em produção, apenas erros críticos
    if (!this.isDevelopment && level === 'error') {
      console.error(message, data);
    }
  }

  private getConsoleStyle(level: LogLevel): string {
    const styles = {
      info: 'color: #3b82f6; font-weight: bold',
      warn: 'color: #f59e0b; font-weight: bold',
      error: 'color: #ef4444; font-weight: bold',
      debug: 'color: #8b5cf6; font-weight: bold',
    };
    return styles[level];
  }

  private getPrefix(level: LogLevel): string {
    const prefixes = {
      info: 'ℹ️ INFO',
      warn: '⚠️ WARN',
      error: '❌ ERROR',
      debug: '🔍 DEBUG',
    };
    return prefixes[level];
  }

  info(message: string, data?: unknown) {
    this.log('info', message, data);
  }

  warn(message: string, data?: unknown) {
    this.log('warn', message, data);
  }

  error(message: string, data?: unknown) {
    this.log('error', message, data);
  }

  debug(message: string, data?: unknown) {
    this.log('debug', message, data);
  }

  getLogs(): LogEntry[] {
    return [...this.logs];
  }

  clearLogs() {
    this.logs = [];
  }

  exportLogs(): string {
    return JSON.stringify(this.logs, null, 2);
  }

  // =====================================================
  // SISTEMA DE AUDITORIA DE TEMAS
  // =====================================================

  /**
   * Valida se uma string é um UUID válido
   */
  private isValidUUID(value: string): boolean {
    const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    return uuidRegex.test(value);
  }

  /**
   * Registra evento de auditoria de tema no banco de dados
   */
  async logThemeAudit(auditLog: ThemeAuditLog): Promise<void> {
    if (!this.themeAuditEnabled) return;

    try {
      // Log no console em desenvolvimento
      const emoji = this.getEventEmoji(auditLog.eventType);
      this.info(`${emoji} Theme Audit: ${auditLog.eventDescription}`, {
        themeId: auditLog.themeId,
        themeName: auditLog.themeName,
        eventType: auditLog.eventType,
        changes: auditLog.changesMade,
      });

      // Garante que theme_id seja um UUID válido ou null
      const themeId =
        auditLog.themeId && this.isValidUUID(auditLog.themeId)
          ? auditLog.themeId
          : null;

      // Mapeia os event_type curtos para os valores aceitos pelo banco
      const eventTypeMap: Record<EventType, string> = {
        loaded: 'theme_loaded',
        modified: 'theme_modified',
        saved: 'theme_saved',
        restored: 'theme_restored',
        deleted: 'theme_deleted',
        created: 'theme_created',
        preview: 'theme_preview',
        error: 'theme_modified',
      };

      const dbEventType = eventTypeMap[auditLog.eventType] ?? 'theme_modified';

      // Persistir no banco de dados
      const { error } = await supabase
        .from('ibx_theme_audit_logs')
        .insert({
          theme_id: themeId,
          theme_name: auditLog.themeName,
          event_type: dbEventType,
          event_description: auditLog.eventDescription,
          user_action: auditLog.userAction || null,
          changes_made: auditLog.changesMade || null,
          snapshot_before: auditLog.snapshotBefore || null,
          snapshot_after: auditLog.snapshotAfter || null,
          metadata: auditLog.metadata || null,
          severity: auditLog.severity || 'info',
        });

      if (error) {
        this.error('Erro ao salvar log de auditoria no banco', error);
      }
    } catch (err) {
      this.error('Erro ao processar log de auditoria', err);
    }
  }

  /**
   * Retorna emoji apropriado para cada tipo de evento
   */
  private getEventEmoji(eventType: EventType): string {
    const emojis: Record<EventType, string> = {
      loaded: '📂',
      modified: '✏️',
      saved: '💾',
      restored: '🔄',
      deleted: '🗑️',
      created: '✨',
      preview: '👁️',
      error: '❌',
    };
    return emojis[eventType] || '📋';
  }

  /**
   * Busca logs de auditoria de um tema específico
   */
  async getThemeAuditLogs(themeId: string, limit = 50): Promise<ThemeAuditLog[]> {
    try {
      const { data, error } = await supabase
        .from('ibx_theme_audit_logs')
        .select('*')
        .eq('theme_id', themeId)
        .order('created_at', { ascending: false })
        .limit(limit);

      if (error) throw error;

      return (data || []).map((log) => ({
        themeId: log.theme_id,
        themeName: log.theme_name,
        eventType: log.event_type,
        eventDescription: log.event_description,
        userAction: log.user_action,
        changesMade: log.changes_made,
        snapshotBefore: log.snapshot_before,
        snapshotAfter: log.snapshot_after,
        metadata: log.metadata,
        severity: log.severity,
      }));
    } catch (err) {
      this.error('Erro ao buscar logs de auditoria', err);
      return [];
    }
  }

  /**
   * Busca todos os logs de auditoria com filtros
   */
  async getAllThemeAuditLogs(filters?: {
    eventType?: EventType;
    severity?: Severity;
    startDate?: Date;
    endDate?: Date;
    limit?: number;
  }): Promise<ThemeAuditLog[]> {
    try {
      let query = supabase
        .from('ibx_theme_audit_logs')
        .select('*')
        .order('created_at', { ascending: false });

      if (filters?.eventType) {
        query = query.eq('event_type', filters.eventType);
      }

      if (filters?.severity) {
        query = query.eq('severity', filters.severity);
      }

      if (filters?.startDate) {
        query = query.gte('created_at', filters.startDate.toISOString());
      }

      if (filters?.endDate) {
        query = query.lte('created_at', filters.endDate.toISOString());
      }

      if (filters?.limit) {
        query = query.limit(filters.limit);
      } else {
        query = query.limit(100);
      }

      const { data, error } = await query;

      if (error) throw error;

      return (data || []).map((log) => ({
        themeId: log.theme_id,
        themeName: log.theme_name,
        eventType: log.event_type,
        eventDescription: log.event_description,
        userAction: log.user_action,
        changesMade: log.changes_made,
        snapshotBefore: log.snapshot_before,
        snapshotAfter: log.snapshot_after,
        metadata: log.metadata,
        severity: log.severity,
      }));
    } catch (err) {
      this.error('Erro ao buscar todos os logs de auditoria', err);
      return [];
    }
  }

  /**
   * Exporta relatório de auditoria em formato JSON
   */
  async exportThemeAuditReport(themeId?: string): Promise<string> {
    try {
      const logs = themeId
        ? await this.getThemeAuditLogs(themeId, 1000)
        : await this.getAllThemeAuditLogs({ limit: 1000 });

      const report = {
        generatedAt: new Date().toISOString(),
        totalLogs: logs.length,
        themeId: themeId || 'all',
        logs: logs,
        summary: {
          byEventType: this.groupByEventType(logs),
          bySeverity: this.groupBySeverity(logs),
        },
      };

      return JSON.stringify(report, null, 2);
    } catch (err) {
      this.error('Erro ao exportar relatório de auditoria', err);
      return JSON.stringify({ error: 'Erro ao gerar relatório' }, null, 2);
    }
  }

  /**
   * Agrupa logs por tipo de evento
   */
  private groupByEventType(logs: ThemeAuditLog[]): Record<string, number> {
    return logs.reduce((acc, log) => {
      acc[log.eventType] = (acc[log.eventType] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);
  }

  /**
   * Agrupa logs por severidade
   */
  private groupBySeverity(logs: ThemeAuditLog[]): Record<string, number> {
    return logs.reduce((acc, log) => {
      const severity = log.severity || 'info';
      acc[severity] = (acc[severity] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);
  }

  /**
   * Limpa logs antigos do banco de dados (manutenção)
   */
  async cleanOldAuditLogs(daysToKeep = 90): Promise<void> {
    try {
      const cutoffDate = new Date();
      cutoffDate.setDate(cutoffDate.getDate() - daysToKeep);

      const { error } = await supabase
        .from('ibx_theme_audit_logs')
        .delete()
        .lt('created_at', cutoffDate.toISOString());

      if (error) throw error;

      this.info(`Logs de auditoria anteriores a ${cutoffDate.toLocaleDateString()} foram removidos`);
    } catch (err) {
      this.error('Erro ao limpar logs antigos', err);
    }
  }

  /**
   * Desabilita temporariamente a auditoria de temas
   */
  disableThemeAudit(): void {
    this.themeAuditEnabled = false;
    this.warn('Auditoria de temas desabilitada');
  }

  /**
   * Habilita a auditoria de temas
   */
  enableThemeAudit(): void {
    this.themeAuditEnabled = true;
    this.info('Auditoria de temas habilitada');
  }
}

export const logger = new Logger();